import { useEffect, useMemo, useState } from "react";
import { MenuItem, TextField, Typography } from "@mui/material";

/**
 * THE CARVE-OUT FOR THIS PRODUCT, agreed at the moment of approving it.
 *
 * `vendors.margin_rate` is what we negotiated with a vendor across everything
 * they sell. It is the right default and the wrong answer for a single
 * product: a vendor on 19% can be worth 25% on one SKU — thin publisher
 * margin, heavy support, a loss-leader — without reopening the whole deal.
 *
 * Two forms, one meaning. Easexpense is merchant of record, so the cut comes
 * OUT of what the buyer pays; it is never added on top:
 *
 *   buyer 100, 20%      -> vendor 80, we keep 20
 *   buyer 100, flat 20  -> vendor 80, we keep 20
 *
 * The flat figure is what WE KEEP per unit, not what the vendor receives —
 * the one thing here that can be read backwards. So the split is shown live
 * against a real price from this submission rather than explained in prose.
 *
 * Reports `{ kind, value }` upward; "inherit" clears any product deal and
 * falls back to the vendor's standing rate. `null` means "not a usable number
 * yet", which the parent treats as "don't let them approve".
 */

const pctLabel = (n) => {
  const v = Number(n) * 100;
  if (!Number.isFinite(v)) return "—";
  return `${Number.isInteger(v) ? v : v.toFixed(2)}%`;
};

const money = (n, currency) => {
  const sym = currency === "USD" ? "$" : currency === "EUR" ? "€" : "₹";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return (
    sym +
    v.toLocaleString(!currency || currency === "INR" ? "en-IN" : "en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

/** Mirror of utils/margin.js deriveVendorPrice — the preview must not drift. */
const vendorGets = (buyerPays, kind, value) => {
  const mkt = Number(buyerPays);
  const v = Number(value);
  if (!Number.isFinite(mkt) || mkt <= 0 || !Number.isFinite(v)) return null;
  const raw = kind === "flat" ? mkt - v : mkt * (1 - v);
  return Math.max(0, Math.round((raw + Number.EPSILON) * 100) / 100);
};

/** How the vendor's standing agreement reads, for the "inherit" option. */
const describe = (agreement, currency) => {
  if (!agreement) return "their standard rate";
  const tail = agreement.source === "platform" ? " (platform default)" : "";
  return agreement.kind === "flat"
    ? `${money(agreement.value, currency)} per unit${tail}`
    : `${pctLabel(agreement.value)}${tail}`;
};

export default function ProductMarginControl({
  /** `margin` from get_vendor_product: { house, effective, vendor, override } */
  margin,
  /** A representative buyer price from this submission, for the live split. */
  samplePrice,
  currency,
  disabled,
  onChange,
}) {
  const vendorAgreement = margin?.vendor || null;
  const override = margin?.override || null;
  // What the vendor asked for on this product, if anything.
  const ask = margin?.request || null;
  const askPending = ask && ask.status === "pending";

  const toField = (m) =>
    m.kind === "rate" ? String(Number(m.value) * 100) : String(m.value);

  // Open on whatever is already agreed. An unanswered ask does NOT preselect
  // itself — agreeing to it has to be a decision somebody made, not a default
  // somebody failed to notice.
  const [kind, setKind] = useState(override ? override.kind : "inherit");
  const [raw, setRaw] = useState(() => (override ? toField(override) : ""));

  const acceptAsk = () => {
    if (!ask) return;
    setKind(ask.kind);
    setRaw(toField(ask));
  };

  // Report upward on every change. `onChange` is an inline arrow at the call
  // site, so depending on it here would re-fire this forever.
  useEffect(() => {
    if (kind === "inherit") {
      onChange?.({ kind: "inherit" });
      return;
    }
    const n = Number(raw);
    if (raw === "" || !Number.isFinite(n)) {
      onChange?.(null);
      return;
    }
    onChange?.({ kind, value: kind === "rate" ? n / 100 : n });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, raw]);

  const applied = useMemo(() => {
    if (kind === "inherit") return vendorAgreement;
    const n = Number(raw);
    if (raw === "" || !Number.isFinite(n)) return null;
    return { kind, value: kind === "rate" ? n / 100 : n };
  }, [kind, raw, vendorAgreement]);

  const invalid =
    kind !== "inherit" &&
    (raw === "" ||
      !Number.isFinite(Number(raw)) ||
      Number(raw) < 0 ||
      (kind === "rate" && Number(raw) >= 100));

  // The house sells as itself — there is no counterparty to agree a cut with.
  if (margin?.house) return null;

  const buyer = Number(samplePrice);
  const vp = applied ? vendorGets(buyer, applied.kind, applied.value) : null;

  return (
    <div style={{ marginTop: 16 }}>
      <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 2 }}>
        Carve-out for this product
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        style={{ display: "block", marginBottom: 8 }}
      >
        Applies to the prices you are approving now and to everything this
        vendor submits for this product from here. Payouts already live are not
        rewritten — those move the next time they submit.
      </Typography>

      {/* The vendor's ask. Shown before the control, because it is the thing
          the reviewer is deciding about. */}
      {ask && (
        <div
          style={{
            border: "1px solid #cbd5e0",
            background: askPending ? "#fffaf0" : "#f7fafc",
            borderRadius: 8,
            padding: "8px 10px",
            marginBottom: 10,
          }}
        >
          <Typography variant="caption" style={{ display: "block", fontWeight: 700 }}>
            {askPending
              ? "This vendor has asked for a different rate on this product"
              : `Earlier request — ${ask.status}`}
          </Typography>
          <Typography variant="caption" style={{ display: "block", marginTop: 2 }}>
            They asked us to keep{" "}
            <strong>
              {ask.kind === "flat"
                ? `${money(ask.value, currency)} per unit`
                : pctLabel(ask.value)}
            </strong>
            {ask.note ? ` — "${ask.note}"` : ""}
          </Typography>
          {askPending && (
            <button
              type="button"
              onClick={acceptAsk}
              disabled={disabled}
              style={{
                marginTop: 6,
                border: "1px solid #2b6cb0",
                background: "transparent",
                color: "#2b6cb0",
                borderRadius: 6,
                padding: "2px 10px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Use what they asked for
            </button>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <TextField
          select
          size="small"
          label="Rate"
          value={kind}
          disabled={disabled}
          onChange={(e) => setKind(e.target.value)}
          style={{ minWidth: 190 }}
        >
          <MenuItem value="inherit">
            Their standard — {describe(vendorAgreement, currency)}
          </MenuItem>
          <MenuItem value="rate">Percentage, this product only</MenuItem>
          <MenuItem value="flat">Flat amount, this product only</MenuItem>
        </TextField>

        {kind !== "inherit" && (
          <TextField
            size="small"
            type="number"
            label={kind === "rate" ? "We keep (%)" : "We keep per unit"}
            value={raw}
            disabled={disabled}
            error={invalid}
            onChange={(e) => setRaw(e.target.value)}
            onWheel={(e) => e.target.blur()}
            inputProps={{ min: 0, step: kind === "rate" ? 0.5 : 1 }}
            style={{ width: 170 }}
          />
        )}
      </div>

      {invalid && (
        <Typography variant="caption" style={{ display: "block", marginTop: 6, color: "#c53030" }}>
          {kind === "rate"
            ? "A percentage carve-out has to sit between 0 and 100."
            : "A flat carve-out cannot be negative."}
        </Typography>
      )}

      {/* The split, on a real price from this submission. This is the part
          that makes "flat" unambiguous: whatever is typed, the reviewer sees
          who ends up with what. */}
      {!invalid && Number.isFinite(buyer) && buyer > 0 && vp !== null && (
        <Typography
          variant="caption"
          style={{ display: "block", marginTop: 8, color: "#2d3748" }}
        >
          Buyer pays <strong>{money(buyer, currency)}</strong> &rarr; vendor
          receives <strong>{money(vp, currency)}</strong>, Easexpense keeps{" "}
          <strong>{money(buyer - vp, currency)}</strong>
          {buyer > 0 ? ` (${pctLabel((buyer - vp) / buyer)})` : ""}.
        </Typography>
      )}
    </div>
  );
}
